import React, {useEffect} from 'react';
import { IProductStorefrontEntry } from '../../models/Search';
import {Checkbox, DropdownMenuItemType, Icon, IconButton, IDropdownOption, TooltipHost} from '@fluentui/react';
import NumberField from '../Inputs/NumberField';
import MarkupTypeSelection from '../Storefront/MarkupTypeSelection';
import {
    useGetGlobalStorefrontSettingsQuery,
    useGetStorefrontProductCountQuery,
    useGetStorefrontsQuery
} from '../../store/endpoints/StorefrontSettingsEndpoints';

import {
    useGetStorefrontProductDetailSectionsQuery, 
    useSaveStorefrontSectionMutation
} from '../../store/endpoints/StorefrontSectionEndpoints';
import {IStorefrontSection, IStorefrontSettings} from '../../models/Storefront';
import { useSelector } from 'react-redux';
import { RootState } from '../../store/rootReducer';
import ToggleField from "../Common/ToggleField";
import StorefrontDetailsPanel from "../Storefront/StorefrontDetailsPanel";
import {IIconProps} from "@fluentui/react/lib-amd/Icon";
import {Dropdown} from "@fluentui/react/lib/Dropdown";
import {
    useGetOrganizationEntitlementsQuery
} from "../../store/endpoints/OrganizationsEndpoints";
import DropdownField from "../Common/DropdownField";
import {defaultShow, isGloballyDisabled, isStorefrontDisabled} from "../../logic/linkedItemsVisibility";
import {sortedPages} from "../Storefront/Editor/pagesConfig";
import {useGetStorefrontPagesQuery} from "../../store/endpoints/StorefrontPageEndpoints";

interface IProductStorefrontDetailsProps {
    editMode?: boolean;
    storefrontOverrideMarkup?: number;
    storefrontOverrideMarkupType?: string;
    storefrontAlwaysAvailable?: boolean;
    showLinkedItemsOnStorefront?: boolean;
    includeInAllStorefronts?: boolean;
    featureInAllStorefronts?: boolean;
    hidePriceOnStorefront?: boolean;
    storefronts?: {[key: string]: IProductStorefrontEntry};
    includedStorefronts?: string[];
    onIncludedStorefrontsChange: (includedStorefronts: string[]) => void;
    featuredStorefronts?: string[];
    onFeaturedStorefrontsChange: (featuredStorefronts: string[]) => void;
    onStorefrontsChange: (storefronts: {[key:string]: IProductStorefrontEntry}) => void;
    onStorefrontOverrideMarkupChange: (markup: number | undefined) => void;
    onStorefrontOverrideMarkupTypeChange: (markupType: string | undefined) => void;
    onStorefrontAlwaysAvailableChange: (checked?: boolean) => void;
    onShowLinkedItemsOnStorefrontChange: (checked?: boolean) => void;
    onStorefrontIncludeInAllStorefrontsChange: (checked?: boolean) => void;
    onStorefrontFeatureInAllStorefrontsChange: (checked?: boolean) => void;
    onHidePriceOnStorefrontChange: (checked?: boolean) => void;
    storefrontOrderPriority?: number;
    onStorefrontOrderPriorityChange: (priority?: number) => void;
    globalStorefrontMinimumPrice?: number;
    onGlobalStorefrontMinimumPriceChange: (price?: number) => void;
    productId: string;
    onChangeAnything: () => void;
}

const ProductStorefrontDetails: React.FC<IProductStorefrontDetailsProps> = (props) => {
    const org = useSelector((state: RootState) => state.settings.selectedOrganization);
    const getOrgEntitlements = useGetOrganizationEntitlementsQuery(org ?? '', {skip: !org || org === ''});
    
    const productCount = useGetStorefrontProductCountQuery();
    const storefronts = useGetStorefrontsQuery();
    const globalStorefrontDetails = useGetGlobalStorefrontSettingsQuery();
    const [reachedLimit, setReachedLimit] = React.useState<boolean>(false);
    const [globalMarkupValue, setGlobalMarkupValue] = React.useState<number | undefined>();
    const [globalMarkupType, setGlobalMarkupType] = React.useState<string | undefined>();
    const sections = useGetStorefrontProductDetailSectionsQuery();
    const [updateStorefrontSection] = useSaveStorefrontSectionMutation();
    const [availableSections, setAvailableSections] = React.useState<IStorefrontSection[]>([]);
    const [selectedSections, setSelectedSections] = React.useState<string[]>()
    
    useEffect(() => {
        if (getOrgEntitlements.data?.storefrontProductLimit != null && productCount.data?.count != null) {
            setReachedLimit(productCount.data.count > getOrgEntitlements.data.storefrontProductLimit);
        }
    }, [org, productCount.data]);

    useEffect(() => {
        if(globalStorefrontDetails.data) {
            setGlobalMarkupValue(globalStorefrontDetails.data.defaultMarkup);
            setGlobalMarkupType(globalStorefrontDetails.data.defaultMarkupType);
        }
    }, [globalStorefrontDetails]);
    useEffect(() => {
        if(sections.data){
            const filteredSections = sections.data.items.filter(section => section.shared);
            setAvailableSections(filteredSections);
            const selectedProductSections = filteredSections.filter(section => section.productIds?.includes(props.productId)).map(section => section.id ?? '');
            setSelectedSections([...selectedProductSections]);
        }
    }, [sections.data]);

    const onChangeSelectedSections = (event: React.FormEvent<HTMLDivElement>, item?: IDropdownOption): void => {
        if (item) {
            props.onChangeAnything()
            const storefrontSection = availableSections.find(section => section.id === item.key);
            if (!storefrontSection) return;
            const key = item.key.toString();
            if (item.selected) {
                setSelectedSections([...(selectedSections ?? []).filter(k => k !== key), key]);
                const newProductIds = [...(storefrontSection.productIds ?? []), props.productId];
                updateStorefrontSection({section: {...storefrontSection, productIds: newProductIds}, storefront: 'global'});
            } else {
                setSelectedSections((selectedSections ?? []).filter(k => k !== key));
                const newProductIds = (storefrontSection.productIds ?? []).filter(id => id !== props.productId);
                updateStorefrontSection({section: {...storefrontSection, productIds: newProductIds}, storefront: 'global'});
            }
        }
    };
    
    return (
        <div>
            <h3>Default Storefront Settings</h3>
            <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
            {reachedLimit && <p>Product limit reached. Please remove products from storefronts to add more.</p>}
                <div style={{maxWidth: '25%', flexGrow: 1}}>
                    <NumberField
                        disabled={!props.editMode || reachedLimit}
                        label='Storefront Override Markup'
                        placeholder={!props.storefrontOverrideMarkup ? `Global Value: (${(globalMarkupValue ?? 'Not Set')})` : ''}
                        value={props.storefrontOverrideMarkup}
                        onChange={(v) => {
                            props.onChangeAnything()
                            props.onStorefrontOverrideMarkupChange(v)
                        }}
                        decimal={true}
                    />
                </div>
                <div style={{maxWidth: '25%', flexGrow: 1}}>
                    <MarkupTypeSelection
                        disabled={!props.editMode || reachedLimit}
                        label='Storefront Override Markup Type'
                        value={ (!props.storefrontOverrideMarkupType || props.storefrontOverrideMarkupType == "") ? `Global Value: (${(globalMarkupType ?? 'Not Set')})` : ''}
                        onChange={(v) => {
                            props.onChangeAnything()
                            props.onStorefrontOverrideMarkupTypeChange(v)
                        }}
                    />
                </div>
                <div style={{maxWidth: '25%', flexGrow: 1}}>
                    <NumberField
                        disabled={!props.editMode || reachedLimit}
                        label='Storefront Sort Priority'
                        value={props.storefrontOrderPriority}
                        onChange={(v) => {
                            props.onChangeAnything()
                            props.onStorefrontOrderPriorityChange(v)
                        }}
                        decimal={false}
                    />
                </div>
                <div style={{maxWidth: '25%', flexGrow: 1}}>
                    <NumberField
                        disabled={!props.editMode || reachedLimit}
                        label='Global Storefront Minimum Price'
                        value={props.globalStorefrontMinimumPrice}
                        onChange={(v) => {
                            props.onChangeAnything()
                            props.onGlobalStorefrontMinimumPriceChange(v)
                        }}
                        decimal={true}
                    />
                </div>
                <div style={{maxWidth: '25%', flexGrow: 1}}>
                    <ToggleField
                        inline={true}
                        disabled={!props.editMode || reachedLimit}
                        label='Always Available'
                        checked={props.storefrontAlwaysAvailable}
                        toolTip={<div>This forces Adaptive Catalog to include this product in the storefront, even if it is not otherwise considered in stock.<br></br><br></br> <b>When enabling this option, it is highly recommended that you set a minimum price, as out of stock items may not include a cost to mark up or may have out of date costs. </b></div>}
                        onChange={(_, v) => {
                            props.onChangeAnything();
                            props.onStorefrontAlwaysAvailableChange(v)
                        }}
                    />
                </div>
                <div style={{maxWidth: '25%', flexGrow: 1}}>
                    {(() => {
                        const globallyDisabled = isGloballyDisabled(globalStorefrontDetails.data);
                        const checkbox = (
                            <Checkbox
                                label='Show Linked Items'
                                disabled={!props.editMode || reachedLimit || globallyDisabled}
                                checked={props.showLinkedItemsOnStorefront ?? defaultShow(globalStorefrontDetails.data)}
                                onChange={(_, v) => {
                                    props.onChangeAnything();
                                    props.onShowLinkedItemsOnStorefrontChange(v)
                                }}
                            />
                        );
                        return globallyDisabled
                            ? <TooltipHost content='Linked items are disabled in Global Storefront Settings.'>{checkbox}</TooltipHost>
                            : checkbox;
                    })()}
                </div>
                <div style={{maxWidth: '25%', flexGrow: 1}}>
                    <ToggleField
                        inline={true}
                        disabled={!props.editMode || reachedLimit}
                        label='Hide Price On Storefront'
                        checked={props.hidePriceOnStorefront}
                        toolTip={<div>When enabled, the storefront shows "Get a Quote" instead of this product's price. Shoppers can add it to their cart but cannot check out — they must request a quote instead.</div>}
                        onChange={(_, v) => {
                            props.onChangeAnything();
                            props.onHidePriceOnStorefrontChange(v)
                        }}
                    />
                </div>
                <div style={{maxWidth: '25%', flexGrow: 1}}>
                    <ToggleField
                        inline={true}
                        disabled={!props.editMode || reachedLimit}
                        label='Include in All Storefronts'
                        checked={props.includeInAllStorefronts}
                        toolTip={<div>This forces Adaptive Catalog to include this product in each storefront unless Ignore Inherited Products is selected on the individual Storefront.<br></br></div>}
                        onChange={(_, v) => {
                            props.onChangeAnything();
                            props.onStorefrontIncludeInAllStorefrontsChange(v)
                        }}
                    />
                </div>

                {props.includeInAllStorefronts &&
                    <div style={{ width: '100%', display: 'block' }}>
                        <div style={{maxWidth: '25%', flexGrow: 1}}>
                            <ToggleField
                                inline={true}
                                disabled={!props.editMode || reachedLimit}
                                label='Feature in All Storefronts'
                                checked={props.featureInAllStorefronts}
                                toolTip={<div>This forces Adaptive Catalog to feature this product in each storefront unless Ignore Inherited Products is selected on the individual Storefront.<br></br></div>}
                                onChange={(_, v) => {
                                    props.onChangeAnything();
                                    props.onStorefrontFeatureInAllStorefrontsChange(v)
                                }}
                            />
                        </div>
                        <div>
                            <Dropdown
                                placeholder={"Select Global Sections"}
                                multiSelect={true}
                                label={"Storefront Global Section Visibility"}
                                selectedKeys={selectedSections}
                                options={availableSections?.map(section => ({ key: section.id ?? '', text: section.title ?? `Section ${section.sortOrder ?? 1}`  })) ?? []}
                                onChange={(e,v) => {
                                    props.onChangeAnything();
                                    onChangeSelectedSections(e, v)}}
                                disabled={sections.isLoading || sections.isFetching || !props.editMode}
                            />
                        </div>
                    </div>
                    
                }
            </div>
            <h3>Specific Storefront Settings</h3>
            <table className='data-table'>
                <thead>
                <tr>
                    <th></th>
                    <th>Storefront</th>
                    <th>Included</th>
                    <th>Featured</th>
                    <th>Markup</th>
                    <th>Markup Type</th>
                    <th>Minimum Price</th>
                    <th>Linked Items</th>
                    <th>Section Visibility</th>
                </tr>
                </thead>
                <tbody>
                {storefronts.data?.items?.map((storefront) =>  (
                    <ProductStorefrontEntryDetails
                        productId={props.productId}
                        includedInAllStorefronts={props.includeInAllStorefronts}
                        featureInAllStorefronts={props.featureInAllStorefronts}
                        reachedLimit={reachedLimit}
                        editMode={props.editMode}
                        key={storefront.id}
                        storefront={storefront}
                        included={props.includedStorefronts?.includes(storefront.id ?? '')}
                        featured={props.featuredStorefronts?.includes(storefront.id ?? '')}
                        onFeaturedChange={(featured) => {
                            props.onChangeAnything();
                            props.onFeaturedStorefrontsChange(featured ? [...(props.featuredStorefronts ?? []), storefront.id ?? ''] : (props.featuredStorefronts ?? []).filter((id) => id !== storefront.id))
                        }}
                        onIncludedChange={(included) => {
                            props.onChangeAnything();
                            props.onIncludedStorefrontsChange(included ? [...(props.includedStorefronts ?? []), storefront.id ?? ''] : (props.includedStorefronts ?? []).filter((id) => id !== storefront.id))
                        }}
                        storefrontEntry={props.storefronts?.[storefront.id ?? '']}
                        onStorefrontEntryChange={(storefrontEntry) => {
                            props.onChangeAnything();
                            props.onStorefrontsChange({...props.storefronts, [storefront.id ?? '']: storefrontEntry})
                        }}
                        productOverrideMarkupType={props.storefrontOverrideMarkupType}
                        productOverrideMarkup={props.storefrontOverrideMarkup}
                    />
                ))}
                </tbody>
            </table>
        </div>
    );
}

interface IProductStorefrontEntryDetailsProps {
    storefront: IStorefrontSettings;
    storefrontEntry?: IProductStorefrontEntry;
    included?: boolean;
    featured?: boolean;
    onIncludedChange?: (included: boolean) => void;
    onFeaturedChange?: (featured: boolean) => void;
    onStorefrontEntryChange: (storefrontEntry: IProductStorefrontEntry) => void;
    editMode?: boolean;
    reachedLimit?: boolean;
    productOverrideMarkupType?: string;
    productOverrideMarkup?: number;
    includedInAllStorefronts?: boolean;
    featureInAllStorefronts?: boolean;
    productId: string;
}

const DEFAULT_STOREFRONT_ENTRY: IProductStorefrontEntry = {
    storefront: '',
    overrideMarkup: 0,
    overrideMarkupType: '',
}

const ProductStorefrontEntryDetails: React.FC<IProductStorefrontEntryDetailsProps> = (props) => {
    const globalStorefrontDetails = useGetGlobalStorefrontSettingsQuery();
    const [detailsPanelOpen, setDetailsPanelOpen] = React.useState<boolean>(false);
    const sections = useGetStorefrontProductDetailSectionsQuery();
    const [updateStorefrontSection] = useSaveStorefrontSectionMutation();
    const [availableSections, setAvailableSections] = React.useState<IStorefrontSection[]>([]);
    const [selectedSections, setSelectedSections] = React.useState<string[]>()
    const [sectionSaveConfirmed, setSectionSaveConfirmed] = React.useState(false);

    const showSectionSaved = () => {
        setSectionSaveConfirmed(true);
        setTimeout(() => setSectionSaveConfirmed(false), 2500);
    };

    // Mirror the API's GetTargetStorefront: with the org-level preview system on, the pages editor
    // creates/edits sections against the "{id}-preview" copy, and publishing re-creates the live
    // copies with NEW ids. The preview copies are therefore the ones to list here and attach
    // products to (the save goes through the same preview-targeting endpoint, and the assignment
    // reaches the live store on the next publish). Filtering on the live id showed only stale
    // published copies and hid newly created sections entirely.
    const sectionsStorefrontId = globalStorefrontDetails.data?.usePreview === true
        ? `${props.storefront.id}-preview`
        : props.storefront.id;

    // Custom pages for grouping the section picker. The pages GET is preview-resolved server-side, so
    // we pass the live storefront id (not the "-preview" variant used for the section list filter).
    const pagesQuery = useGetStorefrontPagesQuery(props.storefront.id ?? '', { skip: !props.storefront.id });

    useEffect(() => {
        if(sections.data){
            const filteredSections = sections.data.items.filter(section => section.storefront == sectionsStorefrontId);
            setAvailableSections(filteredSections);
            const selectedProducts = filteredSections.filter(section => section.productIds?.includes(props.productId)).map(section => section.id ?? '');
            setSelectedSections([...selectedProducts]);
        }
    }, [sections.data, sectionsStorefrontId, props.productId]);
    
    
    const markupChange = (overrideMarkup?:number) => {
        if (props.storefront.id == null) return;
        if (props.storefrontEntry == null) props.onStorefrontEntryChange({...DEFAULT_STOREFRONT_ENTRY, storefront: props.storefront.id, overrideMarkup});
        else props.onStorefrontEntryChange({...props.storefrontEntry, overrideMarkup});
    }
    const markupTypeChange = (overrideMarkupType:string) => {
        if (props.storefront.id == null) return;
        if (props.storefrontEntry == null) props.onStorefrontEntryChange({...DEFAULT_STOREFRONT_ENTRY, storefront: props.storefront.id, overrideMarkupType});
        else props.onStorefrontEntryChange({...props.storefrontEntry, overrideMarkupType});
    }
    const onIncludedChange = (ev?: React.FormEvent<HTMLElement | HTMLInputElement> | undefined, checked?: boolean | undefined) => {
        if (props.onIncludedChange) props.onIncludedChange(checked ?? false);
    }

    const onFeaturedChange = (ev?: React.FormEvent<HTMLElement | HTMLInputElement> | undefined, checked?: boolean | undefined) => {
        if (props.onFeaturedChange) props.onFeaturedChange(checked ?? false);
    }

    const onMinimumPriceChange = (minimumPrice?:number) => {
        if (props.storefront.id == null) return;
        if (props.storefrontEntry == null) props.onStorefrontEntryChange({...DEFAULT_STOREFRONT_ENTRY, storefront: props.storefront.id, minimumPrice});
        else props.onStorefrontEntryChange({...props.storefrontEntry, minimumPrice});
    }

    const onShowLinkedItemsChange = (showLinkedItems?: boolean | null) => {
        if (props.storefront.id == null) return;
        if (props.storefrontEntry == null) props.onStorefrontEntryChange({...DEFAULT_STOREFRONT_ENTRY, storefront: props.storefront.id, showLinkedItems});
        else props.onStorefrontEntryChange({...props.storefrontEntry, showLinkedItems});
    }

    const linkedItemsDisabled = isStorefrontDisabled(globalStorefrontDetails?.data, props.storefront);
    const linkedItemsKey = props.storefrontEntry?.showLinkedItems == null
        ? 'inherit'
        : (props.storefrontEntry.showLinkedItems ? 'show' : 'hide');
    const linkedItemsOptions: IDropdownOption[] = [
        { key: 'inherit', text: 'Inherit' },
        { key: 'show', text: 'Show' },
        { key: 'hide', text: 'Hide' },
    ];
    
    const getMarkupPlaceholder = () => {
        
        if(props.productOverrideMarkup){
            return `Inherit from Product Override: (${props.productOverrideMarkup.toString()})`
        }
        if(props.storefront.defaultMarkup){
            return `${props.storefront.defaultMarkup.toString()} - Inherited from Storefront Default`
        }
        return `${globalStorefrontDetails?.data?.defaultMarkup ?? 'Not Set'} - Inherit from Global`
    }
    
    const getMarkupTypePlaceholder = () => {
        if(props.productOverrideMarkupType && props.productOverrideMarkupType != '') {
            return `Inherit from Product Override (${props.productOverrideMarkupType})`
        }
        if(props.storefront.defaultMarkupType && props.storefront.defaultMarkupType != '') {
            return `Inherit from Storefront Default (${props.storefront.defaultMarkupType})`
        }
        return `Inherit from Global: (${globalStorefrontDetails?.data?.defaultMarkupType ?? 'Percentage'})`

    }

    const editIcon: IIconProps = { iconName: 'edit' };
    const getIncludedState = () => {
        if (props.includedInAllStorefronts) {
            return props.storefront?.ignoreInheritedProducts ? props.included : true;
        }
        return props.included ?? false;
    }
    
    const getFeaturedState = () => {
        if (props.featureInAllStorefronts) {
            return props.storefront?.ignoreInheritedProducts ? props.featured : true;
        }
        return props.featured ?? false;
    }
    
    const getSectionNames = () =>{
        return selectedSections?.map(section => availableSections.find(a => a.id == section)?.title)?.join(',')
    }

    // Section options grouped by home page: main-homepage sections first, then each Custom Page's
    // sections under a header with the page's title (pages come from the storefront's pages resource).
    // Headers only appear when the storefront actually has page-scoped sections, so storefronts
    // without Custom Pages see the flat list exactly as before.
    const getSectionOptions = (): IDropdownOption[] => {
        const toOption = (section: IStorefrontSection): IDropdownOption =>
            ({ key: section.id ?? '', text: section.title ?? `Section ${section.sortOrder ?? 1}` });
        const pages = sortedPages(pagesQuery.data ?? []);
        const mainSections = availableSections.filter(s => !s.page);
        const pageGroups = pages
            .map(page => ({ page, sections: availableSections.filter(s => s.page === page.id) }))
            .filter(group => group.sections.length > 0);
        // Sections referencing a page id that no longer resolves (deleted page) — still selectable.
        const orphaned = availableSections.filter(s => s.page && !pages.some(p => p.id === s.page));
        if (pageGroups.length === 0 && orphaned.length === 0) return mainSections.map(toOption);
        return [
            { key: 'header-main', text: 'Main Homepage', itemType: DropdownMenuItemType.Header },
            ...mainSections.map(toOption),
            ...pageGroups.flatMap(group => [
                { key: `header-${group.page.id}`, text: group.page.title || `/${group.page.slug}`, itemType: DropdownMenuItemType.Header },
                ...group.sections.map(toOption),
            ]),
            ...(orphaned.length > 0 ? [
                { key: 'header-orphaned', text: 'Other Pages', itemType: DropdownMenuItemType.Header },
                ...orphaned.map(toOption),
            ] : []),
        ];
    };

    if (!props.editMode) return (
        <tr>
            <td>
            </td>
            <td>{props.storefront.name}</td>
            <td style={{textAlign: 'center'}}>{getIncludedState() ? '✓' : ''}</td>
            <td style={{textAlign: 'center'}}>{props.featured ? '✓' : ''}</td>
            <td>{getMarkupPlaceholder()}</td>
            <td>
                <MarkupTypeSelection
                    overrideValue={!props.storefrontEntry?.overrideMarkupType || props.storefrontEntry?.overrideMarkupType === '' ? getMarkupTypePlaceholder() : undefined}
                    value={props.storefrontEntry?.overrideMarkupType}
                    disabled={true}
                    
                />
            </td>
            <td>{props.storefrontEntry?.minimumPrice ?? 'Not Set'}</td>
            <td>{linkedItemsDisabled ? 'Disabled' : linkedItemsOptions.find(o => o.key === linkedItemsKey)?.text}</td>
            <td>
                {getSectionNames()}
            </td>
        </tr>
    );

    const onChangeSelectedSections = (event: React.FormEvent<HTMLDivElement>, item?: IDropdownOption): void => {
        if (item) {
            const storefrontSection = availableSections.find(section => section.id === item.key);
            if (!storefrontSection || !props.storefront.id) return;

            let newProductIds = Array.isArray(storefrontSection.productIds)
                ? [...storefrontSection.productIds]
                : [];
            
            if (!newProductIds.includes(props.productId) && item.selected) {
                setSelectedSections([...(selectedSections ?? []).filter(k => k !== item.key.toString()), item.key.toString()]);
                newProductIds.push(props.productId);
                updateStorefrontSection({
                    section: { ...storefrontSection, productIds: newProductIds },
                    storefront: props.storefront.id
                }).unwrap().then(showSectionSaved);
            }
            if (newProductIds.includes(props.productId) && !item.selected) {
                setSelectedSections((selectedSections ?? []).filter(k => k !== item.key.toString()));
                newProductIds = newProductIds.filter(id => id !== props.productId);
                updateStorefrontSection({
                    section: { ...storefrontSection, productIds: newProductIds },
                    storefront: props.storefront.id
                }).unwrap().then(showSectionSaved);
            }
        }
    };

    return (
        <tr>
            {props.storefront.id && 
                <StorefrontDetailsPanel storefrontId={props.storefront.id} globalSettings={globalStorefrontDetails?.data} isOpen={detailsPanelOpen} onClose={() => setDetailsPanelOpen(false)}/>
            }
            <td>
                <IconButton iconProps={editIcon} onClick={() => setDetailsPanelOpen(true)} />
            </td>
            <td>{props.storefront.name}</td>
            <td data-testid={`storefront-included-${props.storefront.name}`}>
                <Checkbox
                    disabled={props.includedInAllStorefronts}
                    checked={getIncludedState()}
                    onChange={onIncludedChange}
                />
            </td>
            <td>
                <Checkbox
                    checked={getFeaturedState()}
                    onChange={onFeaturedChange}
                    disabled={props.reachedLimit || props.featureInAllStorefronts}
                />
            </td>
            <td>
                <NumberField
                    placeholder={getMarkupPlaceholder()}
                    value={props.storefrontEntry?.overrideMarkup}
                    onChange={markupChange}
                    disabled={props.reachedLimit}
                    decimal={true}
                />
            </td>
            <td>
                <MarkupTypeSelection
                    value={props.storefrontEntry?.overrideMarkupType}
                    onChange={markupTypeChange}
                    disabled={props.reachedLimit}
                    overrideValue={props.productOverrideMarkupType && props.productOverrideMarkupType != '' ? `Inherit from Product Override (${props.productOverrideMarkupType})` : undefined}
                />
            </td>
            <td>
                <NumberField
                    value={props.storefrontEntry?.minimumPrice}
                    onChange={onMinimumPriceChange}
                    prefix='$'
                    disabled={props.reachedLimit}
                    decimal={true}
                />
            </td>
            <td>
                {linkedItemsDisabled
                    ? <TooltipHost content='Linked items are disabled for this storefront.'>
                        <Dropdown
                            styles={{root: {minWidth: 90}}}
                            selectedKey={linkedItemsKey}
                            options={linkedItemsOptions}
                            disabled={true}
                        />
                    </TooltipHost>
                    : <Dropdown
                        styles={{root: {minWidth: 90}}}
                        selectedKey={linkedItemsKey}
                        options={linkedItemsOptions}
                        disabled={props.reachedLimit}
                        onChange={(_e, v) => {
                            if (!v) return;
                            onShowLinkedItemsChange(v.key === 'inherit' ? null : v.key === 'show');
                        }}
                    />
                }
            </td>
            <td>
                <div style={{display: 'flex', alignItems: 'center', gap: 4}}>
                    <div style={{minWidth: 250}}>
                        <DropdownField
                            prefixIconName={'refresh'}
                            onPrefixClick={() => sections.refetch()}
                            placeholder={!sections.isLoading && !sections.isFetching && availableSections?.length === 0 ? "No Sections Available" : "Select Sections"}
                            multiSelect={true}
                            selectedKeys={selectedSections}
                            options={getSectionOptions()}
                            onChange={onChangeSelectedSections}
                            disabled={sections.isLoading || sections.isFetching || availableSections?.length === 0}
                        />
                    </div>
                    <div style={{width: '50px'}}>
                    {sectionSaveConfirmed && (
                        <span style={{color: '#107c10', fontSize: 12, whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: 2}}>
                            <Icon iconName='Accept' styles={{root: {fontSize: 11, color: '#107c10'}}} />
                            Saved
                        </span>
                    )}
                    </div>
                </div>
            </td>
        </tr>
    );
}

export default ProductStorefrontDetails;